import { useQuery } from "@tanstack/react-query";
import { useT } from "@/i18n";
import { shortDay, todayInZurich } from "@/lib/format";
import { fetchReturnsExpected, type ReturnEvent } from "@/services/stock";

// His tag's second number for the returns on screen — fetched after the list, kept five minutes.
export function useExpected(ids: number[], enabled = true) {
  const key = [...ids].sort((a, b) => a - b);
  const q = useQuery({
    queryKey: ["stock-returns-expected", key.join(",")], queryFn: () => fetchReturnsExpected(key),
    enabled: enabled && key.length > 0, staleTime: 5 * 60_000,
  });
  return (id: number) => q.data?.expected[String(id)];
}

// The client returns of the planning, as the floor needs them: TODAY first, then the recent ones
// (newest first), then those still to come (soonest first). Nothing comes back before its return
// happens, so a list led by next week is a list read upside down. His screens group the same way:
// "Aujourd'hui · Récents · À venir". Used by Stock's intake and by linking a cart.
export default function ReturnList({ returns, selectedId, onPick, compact = false }: {
  returns: ReturnEvent[]; selectedId?: number | null; onPick: (r: ReturnEvent) => void; compact?: boolean;
}) {
  const { t, locale } = useT();
  const today = todayInZurich();
  const expected = useExpected(returns.map((r) => r.id).slice(0, 60), !compact);
  // His card date: the day and, under it, the weekday.
  const weekday = (iso: string) => (iso ? new Date(`${iso}T12:00:00`).toLocaleDateString(locale, { weekday: "short" }).replace(".", "") : "");
  const day = (r: ReturnEvent) => (r.starts_at || "").slice(0, 10);
  const groups = [
    { key: "today", rows: returns.filter((r) => day(r) === today) },
    { key: "recent", rows: returns.filter((r) => day(r) < today).sort((a, b) => day(b).localeCompare(day(a))) },
    { key: "upcoming", rows: returns.filter((r) => day(r) > today).sort((a, b) => day(a).localeCompare(day(b))) },
  ].filter((g) => g.rows.length > 0);

  return (
    <div className="space-y-3">
      {groups.map((g) => (
        <section key={g.key}>
          <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">{t(`stock.${g.key}`)}</h4>
          <ul className={compact ? "divide-y divide-slate-100" : "grid gap-2 sm:grid-cols-2"}>
            {g.rows.map((r) => (
              <li key={r.id}>
                {compact ? (
                  <button onClick={() => onPick(r)} className="flex w-full items-baseline gap-3 py-2 text-left hover:bg-slate-50">
                    <span className="w-16 shrink-0 text-xs text-slate-500">{shortDay(day(r))}</span>
                    <span className="min-w-0 flex-1 truncate text-sm text-slate-900">{label(r)}</span>
                    <span className="text-xs text-slate-400">#{r.id}</span>
                  </button>
                ) : (
                  <button onClick={() => onPick(r)}
                    className={`w-full rounded-lg px-3 py-2 text-left ring-1 ${selectedId === r.id ? "bg-orange-50 ring-orange-400" : "bg-white ring-slate-200 hover:bg-slate-50"}`}>
                    <span className="flex items-baseline justify-between gap-2">
                      <span className="truncate font-medium text-slate-900">{label(r)}</span>
                      <span className="shrink-0 text-xs text-slate-500">{shortDay(day(r))} {weekday(day(r))}</span>
                    </span>
                    <ReturnMeta r={r} expected={expected(r.id)} />
                  </button>
                )}
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

export function ReturnMeta({ r, expected }: { r: ReturnEvent; expected?: number | null }) {
  const { t } = useT();
  const known = expected != null && expected > 0;
  return (
    <span className="mt-0.5 flex items-baseline gap-2 text-xs text-slate-500">
      <span className="min-w-0 flex-1 truncate">
        {r.type_label} · #{r.id}{r.contract_id ? ` · ${t("stock.contract")} ${r.contract_id}` : ""}
        {!known && r.entered > 0 && <span className="ml-1 text-emerald-700">· {r.entered} {t("stock.entered")}</span>}
      </span>
      {/* His tag: pieces entered / pieces the return brings back. */}
      {known && (
        <span className={`shrink-0 rounded px-1.5 font-medium tabular-nums ${r.entered >= (expected ?? 0) ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}`}>
          {r.entered}/{expected}
        </span>
      )}
    </span>
  );
}

const label = (r: ReturnEvent) => r.client_name || r.contract_name || r.title || `#${r.id}`;
